import { LOCAL_PLAYER_NUMBERS, PlayerNumber } from '~/modules/players/player-number';
import { SelectedPlayerInput } from '~/modules/players/players-manager';
import storage from '~/modules/utils/storage';
import { RemoteMicrophoneInputSource } from '~/routes/select-input/input-sources/remote';

const LAST_SLOT_KEY = 'REMOTE_MIC_LAST_SLOT';

export interface SlotOccupant {
  number: PlayerNumber;
  input: SelectedPlayerInput;
}

/**
 * Which slot each phone was last put in, by its own id.
 *
 * Needed because the slot itself stops pointing at a phone the moment that phone drops: the setup
 * screen repairs a slot whose input cannot be resolved onto a real microphone (see
 * `usePlayerInput`), which is right for the slot and destroys the only record of whose it was.
 * Session storage, the same lifetime as the players list it has to agree with.
 */
const readRemembered = (): Partial<Record<string, PlayerNumber>> =>
  storage.session.getItem<Partial<Record<string, PlayerNumber>>>(LAST_SLOT_KEY) ?? {};

export const rememberSlot = (micId: string, playerNumber: PlayerNumber) => {
  storage.session.setItem(LAST_SLOT_KEY, { ...readRemembered(), [micId]: playerNumber });
};

/** A slot is another phone's only while that phone is still connected - one that has dropped is
 * holding a microphone the room cannot hear, and its own id brings it back to this same slot. */
const heldByAnotherMic = (slot: SlotOccupant | undefined, micId: string, connectedMicIds: readonly string[]): boolean =>
  slot?.input.source === RemoteMicrophoneInputSource.inputName &&
  slot.input.deviceId !== micId &&
  connectedMicIds.includes(slot.input.deviceId ?? '');

/**
 * The slot a phone should be put in when it connects, or null to leave it where it is.
 *
 * A phone that is already in a slot stays in it. One that has been in this room before goes back
 * to the slot it had, so dropping out and scanning again returns it to its own colour and score
 * rather than walking it along the row. Anything else takes the lowest slot no connected phone is
 * using - the count of connections so far is not it, since that skips slots the room has freed.
 *
 * Null when every slot belongs to a phone that is still connected: a phone past the player cap
 * has nowhere to go, and turning up should not evict whoever is already singing.
 */
export const slotForMic = (
  micId: string,
  slots: readonly SlotOccupant[],
  connectedMicIds: readonly string[],
  remembered: Partial<Record<string, PlayerNumber>> = readRemembered(),
): PlayerNumber | null => {
  if (slots.some((slot) => slot.input.deviceId === micId)) return null;

  const at = (playerNumber: PlayerNumber) => slots.find((slot) => slot.number === playerNumber);

  const previous = remembered[micId];
  if (previous !== undefined && !heldByAnotherMic(at(previous), micId, connectedMicIds)) {
    return previous;
  }

  return LOCAL_PLAYER_NUMBERS.find((number) => !heldByAnotherMic(at(number), micId, connectedMicIds)) ?? null;
};

/** The phone to repair a slot onto: one no other slot is using. The first phone in the list was
 * often already seated, and handing it a second slot left its own phone with nowhere to return to. */
export const freePhoneForSlot = <T extends { deviceId: string }>(
  playerNumber: PlayerNumber,
  phones: readonly T[],
  slots: readonly SlotOccupant[],
): T | null =>
  phones.find(
    (phone) => !slots.some((slot) => slot.number !== playerNumber && slot.input.deviceId === phone.deviceId),
  ) ?? null;
